import React, { useCallback, useMemo, useState } from 'react';
import { ContentHeader } from 'components/ui/layout/ContentHeader';
import { useTranslation } from 'react-i18next';
import {
  DarwinFlex,
  DarwinListView,
  StatusChip,
  StatusChipColors,
} from 'components/darwin-ui';
import { getControlDocumentsColumns } from './columns';
import useTableControls from 'hooks/useTableControls';
import { useFloatNotification } from 'contexts/FloatNotificationContext';
import {
  useGetControlDocuments,
  useBulkEditControlDocument,
  useDeleteControlDocument,
  useCreateControlDocument,
  useEditControlDocument,
} from 'api/services/controlDocumentService';
import {
  EditedControlDocumentItem,
  ControlDocument,
  CreateControlDocumentPayload,
} from 'types/controlDocumentsTypes';
import { useUI } from 'contexts/UiContext';
import Icon from 'components/darwin-ui/Icon';
import { GenericDeleteModal } from 'components/ui/GenericDeleteModal';
import { ErrorType } from 'types/genericTypes';
import { CreateOrEditControlDocumentModal } from './CreateOrEditControlDocumentModal';

type EditedControlDocumentItemDraft = {
  aliases?: string[];
  active?: boolean;
};

export const ControlDocuments = () => {
  const { t } = useTranslation();
  const { selectedCompany } = useUI();
  const { openNotification } = useFloatNotification();

  // Mutations
  const { mutateAsync: createControlDocument } = useCreateControlDocument();
  const { mutateAsync: editControlDocument } = useEditControlDocument();
  const bulkEditControlDocumentMutation = useBulkEditControlDocument();
  const {
    mutateAsync: deleteControlDocument,
    isPending: isDeleteControlDocumentPending,
  } = useDeleteControlDocument();

  const { searchQuery, sortOrder, onTableChange } = useTableControls({
    defaultSortBy: 'name',
  });

  const {
    data: controlDocumentsData,
    isLoading: isLoadingControlDocuments,
    isFetching: isFetchingControlDocuments,
    isPending: isPendingControlDocuments,
    error: controlDocumentsError,
  } = useGetControlDocuments({
    companyId: selectedCompany || '',
    sortOrder,
    limit: 100,
  });

  // States for modals
  const [controlDocumentToDelete, setControlDocumentToDelete] = useState<
    string | undefined
  >(undefined);

  const [createOrEditControlDocument, setCreateOrEditControlDocument] =
    useState<{
      type: 'add' | 'edit';
      initialField?: ControlDocument;
    }>();

  // Unified state for all edits
  const [editedItems, setEditedItems] = useState<
    Record<string, EditedControlDocumentItemDraft>
  >({});

  // Show error notification
  if (controlDocumentsError?.message) {
    openNotification('Error', t('control-documents.loading-error'), 'error');
  }

  const tableData = useMemo(
    () => controlDocumentsData?.data ?? [],
    [controlDocumentsData]
  );
  const isLoading =
    isLoadingControlDocuments ||
    isFetchingControlDocuments ||
    isPendingControlDocuments;

  // Clear edits when fresh data matches our edited values
  React.useEffect(() => {
    if (tableData.length > 0 && Object.keys(editedItems).length > 0) {
      const shouldClearEdits = Object.keys(editedItems).every(id => {
        const original = tableData.find(item => String(item.id) === id);
        const edited = editedItems[id];
        if (!original || !edited) return false;

        const aliasesMatch =
          edited.aliases === undefined ||
          JSON.stringify(original.aliases?.sort()) ===
            JSON.stringify(edited.aliases.sort());
        const activeMatch =
          edited.active === undefined || original.isActive === edited.active;

        return aliasesMatch && activeMatch;
      });
      if (shouldClearEdits) {
        setEditedItems({});
      }
    }
  }, [tableData, editedItems]);

  const handleAliasesChange = useCallback(
    (id: string, newAliases: string[]) => {
      setEditedItems(prev => {
        const original = tableData.find(item => String(item.id) === id);
        if (!original) return prev;

        const originalAliases = original.aliases ?? [];
        const hasChanged =
          JSON.stringify(originalAliases.sort()) !==
          JSON.stringify(newAliases.sort());

        if (!hasChanged) {
          // Remove aliases edit if reverted to original
          const { aliases, ...rest } = prev[id] || {};
          if (Object.keys(rest).length === 0) {
            const { [id]: _removed, ...remaining } = prev;
            return remaining;
          }
          return { ...prev, [id]: rest };
        }

        return {
          ...prev,
          [id]: {
            ...prev[id],
            aliases: newAliases,
          },
        };
      });
    },
    [tableData]
  );

  const handleStatusToggle = useCallback(
    (id: string) => {
      setEditedItems(prev => {
        const original = tableData.find(item => String(item.id) === id);
        if (!original) return prev;

        const currentActive = prev[id]?.active ?? original.isActive;
        const newActive = !currentActive;

        if (newActive === original.isActive) {
          // Remove status edit if reverted to original
          const { active, ...rest } = prev[id] || {};
          if (Object.keys(rest).length === 0) {
            const { [id]: _removed, ...remaining } = prev;
            return remaining;
          }
          return { ...prev, [id]: rest };
        }

        return {
          ...prev,
          [id]: {
            ...prev[id],
            active: newActive,
          },
        };
      });
    },
    [tableData]
  );

  // Memoized payload - only edited items
  const savePayload = useMemo(() => {
    const controlDocuments: EditedControlDocumentItem[] = Object.entries(
      editedItems
    )
      .map(([controlDocumentId, draft]) => {
        const original = tableData.find(
          item => String(item.id) === controlDocumentId
        );
        if (!original) return null;

        return {
          controlDocumentId,
          aliases:
            draft.aliases !== undefined ? draft.aliases : original.aliases,
          active: draft.active !== undefined ? draft.active : original.isActive,
        };
      })
      .filter(Boolean) as EditedControlDocumentItem[];

    return { controlDocuments };
  }, [editedItems, tableData]);

  const rowActions = useCallback(
    (item: ControlDocument) => {
      const key = String(item.id);
      const isCurrentlyActive = editedItems[key]?.active ?? item.isActive;

      return [
        {
          key: 'toggleActive',
          label: (
            <StatusChip
              label={
                isCurrentlyActive
                  ? t('control-documents.actions.deactivate')
                  : t('control-documents.actions.activate')
              }
              color={
                isCurrentlyActive
                  ? StatusChipColors.red
                  : StatusChipColors.green
              }
            />
          ),
          onClick: () => handleStatusToggle(key),
        },
        {
          key: 'edit',
          label: (
            <DarwinFlex
              gap={8}
              align="center"
              onClick={() => {
                setCreateOrEditControlDocument({
                  type: 'edit',
                  initialField: item,
                });
              }}
            >
              <Icon name={'edit'} size="sm" color="inherit" />
              {t('list-view.action-items.edit')}
            </DarwinFlex>
          ),
        },
        {
          key: 'delete',
          label: (
            <DarwinFlex
              gap={8}
              align="center"
              onClick={() => setControlDocumentToDelete(key)}
            >
              <Icon name={'delete'} size="sm" color="inherit" />
              {t('list-view.action-items.delete')}
            </DarwinFlex>
          ),
        },
      ];
    },
    [editedItems, handleStatusToggle, t]
  );

  const columns = useMemo(
    () =>
      getControlDocumentsColumns(
        t,
        rowActions,
        searchQuery,
        editedItems as unknown as Record<string, EditedControlDocumentItem>, // only aliases/active used
        handleAliasesChange
      ),
    [t, rowActions, searchQuery, editedItems, handleAliasesChange]
  );

  const getInactiveRow = useCallback(
    (controlDoc: ControlDocument) => {
      const editedItem = editedItems[String(controlDoc.id)];

      return editedItem
        ? !editedItems[String(controlDoc.id)]?.active
        : !controlDoc.isActive;
    },
    [editedItems]
  );

  // Handlers
  const onCreateOrEditSubmit = useCallback<{
    (formData: CreateControlDocumentPayload): Promise<unknown>;
  }>(
    async formData => {
      if (createOrEditControlDocument?.type === 'add') {
        return createControlDocument({
          payload: formData,
          companyId: selectedCompany || '',
        });
      } else {
        return editControlDocument({
          payload: formData,
          companyId: selectedCompany || '',
          controlDocumentId:
            createOrEditControlDocument?.initialField?.id || '',
        });
      }
    },
    [
      createControlDocument,
      createOrEditControlDocument?.initialField?.id,
      createOrEditControlDocument?.type,
      editControlDocument,
      selectedCompany,
    ]
  );

  const handleSave = useCallback(async () => {
    if (savePayload.controlDocuments.length === 0) return;

    try {
      await bulkEditControlDocumentMutation.mutateAsync({
        payload: { controlDocuments: savePayload.controlDocuments },
        companyId: selectedCompany || '',
      });
      openNotification(
        t('success'),
        t('control-documents.save-success'),
        'success'
      );
    } catch (error) {
      openNotification(t('error'), t('control-documents.save-error'), 'error');
    }
  }, [
    savePayload,
    bulkEditControlDocumentMutation,
    selectedCompany,
    openNotification,
    t,
  ]);

  // Misc
  const hasChanges = Object.keys(editedItems).length > 0;
  const isSaving = bulkEditControlDocumentMutation.isPending;

  return (
    <>
      <ContentHeader
        title={t('control-documents.title')}
        onSave={handleSave}
        saveDisabled={!hasChanges || isSaving}
        displayExtraButton={true}
        onExtraButtonClick={() =>
          setCreateOrEditControlDocument({
            type: 'add',
          })
        }
        extraButtonTitle={t('control-documents.create-document')}
        extraButtonDisabled={isSaving}
      />

      <DarwinListView<ControlDocument>
        data={tableData}
        columns={columns}
        isLoading={isLoading}
        table={{
          scrollable: false,
          onChange: onTableChange,
        }}
        behavior={{
          isRowInactive: getInactiveRow,
        }}
      />

      <GenericDeleteModal
        open={!!controlDocumentToDelete}
        onCancel={() => setControlDocumentToDelete(undefined)}
        onOk={() => {
          deleteControlDocument({
            controlDocumentId: controlDocumentToDelete || '',
            companyId: selectedCompany || '',
          })
            .then(() => {
              openNotification(
                t('success'),
                t('control-documents.delete-success'),
                'success'
              );
            })
            .catch(err => {
              const apiError = err as ErrorType;
              if (apiError.body?.errorCode) {
                openNotification(
                  t('error'),
                  t(apiError.body.errorCode),
                  'error'
                );
              } else {
                openNotification(
                  t('error'),
                  t('control-documents.delete-error'),
                  'error'
                );
              }
            })
            .finally(() => {
              setControlDocumentToDelete(undefined);
            });
        }}
        confirmLoading={isDeleteControlDocumentPending}
        bodyText={t('control-documents.delete-modal-body')}
      />

      <CreateOrEditControlDocumentModal
        type={createOrEditControlDocument?.type || 'add'}
        open={!!createOrEditControlDocument}
        closeModal={() => setCreateOrEditControlDocument(undefined)}
        initialField={createOrEditControlDocument?.initialField}
        onSubmit={onCreateOrEditSubmit}
      />
    </>
  );
};
